import { ColDef, ICellRendererParams } from 'ag-grid-community'
import { cellWarningStyle, LinkButton, Table } from 'components/core-v2'
import { formatCurrencyFull } from 'utilities/currency'

import { AllAssetsLadderScenarioResult, ScenarioPoint } from '@/api'
import { CURRENCY } from '@/const'
import { displayPercentage } from '@/utilities/number'

import classes from './scenario-table.module.scss'

export type ScenarioLadder = {
  isinCode: string
  values: ScenarioPoint[]
}

export const ISINCell = ({ data: { isin, onAssetClick } }: ICellRendererParams<any>) => (
  <div className={classes.isinCell}>
    <LinkButton onClick={() => onAssetClick(isin)}>{isin}</LinkButton>
  </div>
)

const COLUMN_DEFS_BASE: ColDef[] = [
  {
    headerName: 'Asset name',
    field: 'isin',
    minWidth: 180,
    pinned: true,
    cellRenderer: ISINCell,
    cellStyle: params => {
      if (!params.data.isin)
        return {
          color: 'white',
          background: 'var(--White10)'
        }
      return {}
    }
  }
]

const getColumnDefs = (scenarioLadder: ScenarioLadder[]) => {
  const ladderDefs =
    scenarioLadder && scenarioLadder.length > 0
      ? scenarioLadder[0].values
          .reduce((acc, item) => {
            if (item.coordinate.bump !== 0) {
              acc.push({
                headerName: displayPercentage(item.coordinate?.bump, 0),
                field: displayPercentage(item.coordinate?.bump, 0),
                type: 'rightAligned',
                minWidth: 60,
                maxWidth: 140,
                flex: 0,
                cellStyle: params => {
                  if (!params.data.isin)
                    return {
                      color: 'white',
                      background: 'var(--White10)'
                    }
                  if (params.value[0] === '-') {
                    return cellWarningStyle
                  }
                  return null
                }
              })
            }
            return acc
          }, [])
          .reverse()
      : []
  return [...COLUMN_DEFS_BASE, ...ladderDefs]
}

const DEFAULT_COLUMN_OPTIONS: ColDef = {
  sortable: true,
  unSortIcon: true
}

export interface ScenarioTableProps {
  allAssetsLadderData: AllAssetsLadderScenarioResult
  data: ScenarioLadder[]
  onAssetClick: (assetId: string) => void
  onRowClick: (selectedRow: any) => void
}

const ScenarioTable = ({
  allAssetsLadderData,
  data,
  onAssetClick,
  onRowClick
}: ScenarioTableProps) => {
  const rowData = data.map(row =>
    row.values.reduce(
      (acc, val) => {
        acc[displayPercentage(val.coordinate.bump, 0)] = formatCurrencyFull(val.value, CURRENCY)
        return acc
      },
      { isin: row.isinCode, onRowClick, onAssetClick }
    )
  )

  const allAssetsLadderRow = [...allAssetsLadderData.values]
    .reverse()
    .slice(1)
    .reduce((acc, column) => {
      acc[displayPercentage(column.coordinate.bump, 0)] = formatCurrencyFull(column.value, CURRENCY)
      return acc
    }, {})

  return (
    <Table
      agGridProps={{
        columnDefs: getColumnDefs(data),
        defaultColDef: DEFAULT_COLUMN_OPTIONS,
        headerHeight: 60,
        rowData: [allAssetsLadderRow, ...rowData],
        rowHeight: 44
      }}
      height="600px"
    />
  )
}
export default ScenarioTable
